import { useEffect, useRef, useState } from "react";
import { CheckSquare, Download, FileText, Upload } from "lucide-react";
import { AdminBookingAPI } from "../../services/adminBooking.api";
import { AdminEventAPI } from "../../services/adminEvent.api";

export default function AdminExport() {
  const [events, setEvents] = useState([]);

  // -- Booking export state --
  const [eventId, setEventId] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [loadingExport, setLoadingExport] = useState(false);

  // -- Attendance export state --
  const [attEventId, setAttEventId] = useState("");
  const [attSort, setAttSort] = useState("newest");
  const [loadingAtt, setLoadingAtt] = useState(false);

  // -- Bulk check-in state --
  const [csvFile, setCsvFile] = useState(null);
  const [loadingCheckin, setLoadingCheckin] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);
  const [checkinError, setCheckinError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    AdminEventAPI.list()
      .then((res) => setEvents(res.data?.items || []))
      .catch(() => {});
  }, []);

  async function handleExport() {
    setLoadingExport(true);
    try {
      const params = {
        sort,
        ...(eventId && { eventId }),
        ...(paymentStatus && { paymentStatus }),
      };
      const res = await AdminBookingAPI.exportCSV(params);
      triggerDownload(res.data, buildFilename("bookings", eventId, paymentStatus));
    } catch {
      alert("Gagal export CSV. Coba lagi.");
    } finally {
      setLoadingExport(false);
    }
  }

  async function handleAttendanceExport() {
    setLoadingAtt(true);
    try {
      const params = {
        sort: attSort,
        ...(attEventId && { eventId: attEventId }),
      };
      const res = await AdminBookingAPI.exportAttendance(params);
      triggerDownload(res.data, buildFilename("absensi", attEventId, "PAID"));
    } catch {
      alert("Gagal export daftar absensi. Coba lagi.");
    } finally {
      setLoadingAtt(false);
    }
  }

  async function handleBulkCheckin() {
    if (!csvFile) return;
    setLoadingCheckin(true);
    setCheckinResult(null);
    setCheckinError("");
    try {
      const res = await AdminBookingAPI.bulkCheckin(csvFile);
      setCheckinResult(res.data?.data);
      setCsvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setCheckinError(err.response?.data?.message || "Gagal proses check-in. Pastikan format CSV benar.");
    } finally {
      setLoadingCheckin(false);
    }
  }

  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function buildFilename(prefix, evId, status) {
    const eventLabel = events.find((e) => e._id === evId)?.title || "semua-event";
    const statusLabel = status || "semua-status";
    return `${prefix}-${eventLabel}-${statusLabel}-${Date.now()}`
      .toLowerCase()
      .replace(/\s+/g, "-") + ".csv";
  }

  return (
    <div className="admin-export">
      <div className="admin-export__header">
        <h1>Export Data</h1>
        <p>Download data booking atau daftar absensi dalam format CSV</p>
      </div>

      {/* ── Booking CSV ── */}
      <div className="admin-export__card">
        <div className="export-card-header">
          <FileText size={20} />
          <span>Export Bookings CSV</span>
        </div>

        <p className="export-desc">
          File CSV berisi: Booking ID, Tanggal, Nama, Email, No HP, Event, Tier,
          Jumlah Tiket, Total, Status Pembayaran, Status Redeem, Kode Tiket.
        </p>

        <div className="export-filters">
          <div className="export-field">
            <label>Event</label>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Semua Event</option>
              {events.map((ev) => (
                <option key={ev._id} value={ev._id}>{ev.title}</option>
              ))}
            </select>
          </div>

          <div className="export-field">
            <label>Status Pembayaran</label>
            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
              <option value="">Semua Status</option>
              <option value="PAID">Paid</option>
              <option value="PENDING">Pending</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <div className="export-field">
            <label>Urutan</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Terbaru dulu</option>
              <option value="oldest">Terlama dulu</option>
            </select>
          </div>
        </div>

        <button className="export-btn" onClick={handleExport} disabled={loadingExport}>
          <Download size={16} />
          {loadingExport ? "Memproses..." : "Download CSV"}
        </button>

        <p className="export-note">
          Tip: Filter per event + PAID untuk daftar hadir hari-H.
        </p>
      </div>

      {/* ── Attendance Export ── */}
      <div className="admin-export__card admin-export__card--attendance">
        <div className="export-card-header">
          <CheckSquare size={20} />
          <span>Export Daftar Absensi</span>
        </div>

        <p className="export-desc">
          CSV khusus hari-H: per baris = 1 tiket (Nama, Tier, Kode Tiket, kolom{" "}
          <strong>Hadir</strong> kosong). Print atau buka di Excel, isi kolom Hadir
          dengan <strong>Y</strong> untuk yang hadir, lalu upload kembali di bawah.
        </p>

        <div className="export-filters">
          <div className="export-field">
            <label>Event</label>
            <select value={attEventId} onChange={(e) => setAttEventId(e.target.value)}>
              <option value="">Semua Event</option>
              {events.map((ev) => (
                <option key={ev._id} value={ev._id}>{ev.title}</option>
              ))}
            </select>
          </div>

          <div className="export-field">
            <label>Urutan</label>
            <select value={attSort} onChange={(e) => setAttSort(e.target.value)}>
              <option value="newest">Terbaru dulu</option>
              <option value="oldest">Terlama dulu</option>
            </select>
          </div>
        </div>

        <button className="export-btn export-btn--green" onClick={handleAttendanceExport} disabled={loadingAtt}>
          <Download size={16} />
          {loadingAtt ? "Memproses..." : "Download Daftar Absensi"}
        </button>

        <p className="export-note">Hanya menampilkan booking berstatus PAID.</p>
      </div>

      {/* ── Bulk Check-in Upload ── */}
      <div className="admin-export__card admin-export__card--checkin">
        <div className="export-card-header">
          <Upload size={20} />
          <span>Upload Absensi (Bulk Check-in)</span>
        </div>

        <p className="export-desc">
          Upload kembali file daftar absensi yang sudah diisi. Semua tiket dengan
          kolom <strong>Hadir = Y</strong> akan otomatis ter-check-in tanpa perlu
          scan satu per satu.
        </p>

        <div className="checkin-upload-area">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            id="checkin-csv"
            className="checkin-file-input"
            onChange={(e) => {
              setCsvFile(e.target.files[0] || null);
              setCheckinResult(null);
              setCheckinError("");
            }}
          />
          <label htmlFor="checkin-csv" className="checkin-file-label">
            {csvFile ? (
              <span className="checkin-file-name">{csvFile.name}</span>
            ) : (
              <>
                <Upload size={18} />
                <span>Pilih file CSV</span>
              </>
            )}
          </label>
        </div>

        <button
          className="export-btn export-btn--upload"
          onClick={handleBulkCheckin}
          disabled={!csvFile || loadingCheckin}
        >
          <CheckSquare size={16} />
          {loadingCheckin ? "Memproses..." : "Proses Bulk Check-in"}
        </button>

        {checkinError && (
          <div className="checkin-result checkin-result--error">{checkinError}</div>
        )}

        {checkinResult && (
          <div className="checkin-result checkin-result--success">
            <div className="checkin-result__row">
              <span>Berhasil check-in</span>
              <strong className="checkin-result__count checkin-result__count--green">
                {checkinResult.checkedIn}
              </strong>
            </div>
            <div className="checkin-result__row">
              <span>Sudah check-in sebelumnya</span>
              <strong>{checkinResult.alreadyUsed}</strong>
            </div>
            <div className="checkin-result__row">
              <span>Kode tidak ditemukan</span>
              <strong>{checkinResult.notFound}</strong>
            </div>
            <div className="checkin-result__row checkin-result__row--total">
              <span>Total diproses</span>
              <strong>{checkinResult.total}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
